import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { isEmpty } from 'underscore';
import { sendLoyaltyModalEvent } from 'common/util/gtm-helper';
import defaultStyles from 'commonStyles/components/coupon/_loyalty';
import styleOverrides from 'styles/components/coupon/_loyalty';
import useStyleOverrides from 'common/components/hooks/use-style-overrides';
import { translate } from 'common/localization';
import BlankAvatar from 'common/icons/loyalty/blank-avatar';
import Button from 'common/components/atoms/button';
import ErrorBox from './coupon-error-box';
import AlertBox from './alert-box';
import { isEmployeeEmail } from 'common/util/loyalty/sign-up';

const LoyaltyCouponSignUp = ({
  disclaimers,
  claimLoyaltyCoupon,
  isLoading,
  error,
  profile,
}) => {
  const styles = useStyleOverrides(defaultStyles, styleOverrides);
  const siteAbbr = BC.site.code === 'competitivecyclist' ? 'cc' : 'bc';
  const {
    handleSubmit,
    control,
    formState,
  } = useForm();
  const isEmployee = isEmployeeEmail(profile.login);
  const isApplyingCoupon = isSubmitting || isLoading;
  const { isSubmitting, errors } = formState;
  const handleClaimLoyaltyCoupon = () => {
    sendLoyaltyModalEvent('join now click', 'nonmember');
    claimLoyaltyCoupon();
  };
  // This implementation is because formState.errors will not trigger the useEffect and formState triggers useEffect twice.
  const triggerErrorsEffect = JSON.stringify(errors);

  useEffect(() => {
    if (!isEmpty(errors)) {
      Object.keys(errors).forEach((key) => {
        sendLoyaltyModalEvent('error', `nonmember|${errors[key].message}`);
      });
    }
  }, [triggerErrorsEffect, errors]);

  return (
    <>
      <form onSubmit={handleSubmit(handleClaimLoyaltyCoupon)} noValidate>
        <div className={styles.userBanner}>
          <div className={styles.hDivider} />
          <div className={styles.userInfo}>
            {profile.imagePath ? (
              <img
                alt=""
                className={styles.profileImage}
                src={profile.imagePath}
              />
            ) : (
              <BlankAvatar className={styles.profileImage} />
            )}
            <div className={styles.userTextWrapper}>
              <span className={styles.userName}>{profile.displayName}</span>
              <span className={styles.userEmail}>{profile.login}</span>
            </div>
          </div>
          <div className={styles.hDivider} />
        </div>
        {Object.keys(errors).length > 0 && (
          <ErrorBox
            description={translate('label.loyalty.coupon.correctErrors')}
            className={styles.errorBox}
          />
        )}
        {Object.keys(error).length > 0 && (
          <ErrorBox
            title={error.title}
            description={error.description}
            className={styles.errorBox}
          />
        )}
        <div className={styles.agreeCheckboxWrapper}>
          <Controller
            render={({ field }) => (
              <input
                type="checkbox"
                className={`checkbox-hidden ${styles.checkboxInput}`}
                id="loyalty-signup-terms"
                onChange={(e) => {
                  field.onChange(e.target.checked);
                }}
              />
            )}
            control={control}
            rules={{
              required: translate('label.loyalty.coupon.error.agreeToTerms'),
            }}
            name="termsAndCondition"
          />
          <label
            className={`checkbox-label ${
              styles.checkboxLabel
            } ${errors.termsAndCondition && styles.checkboxTickError}`}
            htmlFor="loyalty-signup-terms"
          >
            <div className={styles.checkboxLabelWrapper}>
              <span>
                {`${translate('label.loyalty.coupon.agreementExpedition')} `}
                <a href={`/sc/${siteAbbr}-expedition-perks-loyalty-program-terms-conditions`} id="expedition_perks_link" target="_blank" rel="noopener noreferrer">
                  {translate('label.loyalty.coupon.termsAndCondition')}
                </a>
                {errors.termsAndCondition && (
                  <p className={styles.formLabelError}>
                    {translate('label.loyalty.coupon.error.agreeToTerms')}
                  </p>
                )}
              </span>
            </div>
          </label>
        </div>
        {isEmployee && (
          <AlertBox
            title={translate('alertbox.title')}
            description={translate('alertbox.description')}
          />
        )}

        <Button
          className={styles.modalJoinBtn}
          type="submit"
          primary
          disabled={isEmployee || isApplyingCoupon}
        >
          {isApplyingCoupon ? (
            <div className="loading-primary" />
          ) : (
            <span>{translate('label.loyalty.coupon.joinNow')}</span>
          )}
        </Button>
      </form>

      <div className={styles.disclaimer}>
        <span>{disclaimers.recognized}</span>
      </div>
    </>
  );
};

LoyaltyCouponSignUp.propTypes = {
  disclaimers: PropTypes.object.isRequired,
  claimLoyaltyCoupon: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.object.isRequired,
  profile: PropTypes.shape({
    imagePath: PropTypes.string,
    displayName: PropTypes.string.isRequired,
    login: PropTypes.string.isRequired,
  }).isRequired,
};

export default LoyaltyCouponSignUp;
